import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  Chip,
  Divider,
  Tooltip,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Switch,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Player, PopupStyle, PopupTargetType, PopupItem } from "../types";
import { POPUP_STYLE_LIST, POPUP_STYLE_CONFIG, PopupHeader } from "../utils/popupStyles";
import { apiRequest } from "../config/api";
import ImageUploadService from "../services/imageUpload";

interface PopupEditorPanelProps {
  variant: "player" | "public";
  apiListPath: string;
  players?: Player[];
  divisionOptions?: string[];
}

// Shared create-form + list UI for both the "Player Pop-ups" and "Public
// Homepage Pop-ups" admin tabs. The two variants differ only in whether
// audience targeting and per-player acknowledgment tracking apply.
const PopupEditorPanel: React.FC<PopupEditorPanelProps> = ({
  variant,
  apiListPath,
  players = [],
  divisionOptions = [],
}) => {
  const [popups, setPopups] = useState<PopupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("Open link");
  const [style, setStyle] = useState<PopupStyle>(variant === "public" ? "congrats" : "notice");
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [targetType, setTargetType] = useState<PopupTargetType>("all");
  const [targetPlayers, setTargetPlayers] = useState<Player[]>([]);
  const [targetDivisions, setTargetDivisions] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [expandedPopup, setExpandedPopup] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPopups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiListPath]);

  const fetchPopups = async () => {
    try {
      setLoading(true);
      const res = await apiRequest(apiListPath);
      if (!res.ok) throw new Error("Failed to load pop-ups");
      setPopups(await res.json());
    } catch (err: any) {
      setError(err.message || "Failed to load pop-ups");
    } finally {
      setLoading(false);
    }
  };

  const audienceLabel = (): string => {
    if (variant === "player") {
      if (targetType === "players") return `${targetPlayers.length} Player${targetPlayers.length === 1 ? "" : "s"}`;
      if (targetType === "division") return targetDivisions.length ? `Division(s): ${targetDivisions.join(", ")}` : "Division(s)";
      return "All Players";
    }
    return "Everyone (Public Homepage)";
  };

  const isTargetValid = (): boolean => {
    if (variant !== "player") return true;
    if (targetType === "players") return targetPlayers.length > 0;
    if (targetType === "division") return targetDivisions.length > 0;
    return true;
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      setError("");
      const url = await ImageUploadService.uploadPopupImage(file);
      setImageUrl(url);
    } catch (err: any) {
      setError(err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setTitle("");
    setMessage("");
    setLinkUrl("");
    setLinkLabel("Open link");
    setStyle(variant === "public" ? "congrats" : "notice");
    setImageUrl(undefined);
    setTargetType("all");
    setTargetPlayers([]);
    setTargetDivisions([]);
  };

  const handleCreate = async () => {
    if (!title.trim() || !message.trim()) {
      setError("Title and message are required");
      return;
    }
    if (!isTargetValid()) {
      setError("Select at least one target for this pop-up");
      return;
    }
    try {
      setCreating(true);
      setError("");
      setSuccess("");
      const body: any = {
        title: title.trim(),
        message: message.trim(),
        linkUrl: linkUrl.trim(),
        linkLabel: linkLabel.trim() || "Open link",
        style,
        ...(imageUrl ? { imageUrl } : {}),
      };
      if (variant === "player") {
        body.targetType = targetType;
        if (targetType === "players") body.targetPlayerIds = targetPlayers.map((p) => p.id);
        if (targetType === "division") body.targetDivisions = targetDivisions;
      }
      const res = await apiRequest(apiListPath, { method: "POST", body: JSON.stringify(body) });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to create pop-up");
      }
      const audience = audienceLabel();
      resetForm();
      setSuccess(`Pop-up created and is now active for ${audience}.`);
      fetchPopups();
    } catch (err: any) {
      setError(err.message || "Failed to create pop-up");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await apiRequest(`${apiListPath}/${id}`, { method: "PATCH", body: JSON.stringify({ isActive }) });
      setPopups((prev) => prev.map((p) => (p.id === id ? { ...p, isActive } : p)));
    } catch {
      setError("Failed to update pop-up");
    }
  };

  const handleDelete = async (id: string) => {
    const confirmMsg =
      variant === "player"
        ? "Delete this pop-up? This will also remove all acknowledgment records."
        : "Delete this pop-up?";
    if (!window.confirm(confirmMsg)) return;
    try {
      await apiRequest(`${apiListPath}/${id}`, { method: "DELETE" });
      setPopups((prev) => prev.filter((p) => p.id !== id));
      setSuccess("Pop-up deleted.");
    } catch {
      setError("Failed to delete pop-up");
    }
  };

  const popupAudienceLabel = (popup: PopupItem): string => {
    const t = popup.targetType || "all";
    if (t === "players") return `${(popup.targetPlayerIds || []).length} Player(s)`;
    if (t === "division") return `Division: ${(popup.targetDivisions || []).join(", ")}`;
    return "All players";
  };

  const popupAudienceTooltip = (popup: PopupItem): string => {
    if ((popup.targetType || "all") !== "players") return "";
    return (popup.targetPlayerIds || []).map((id) => players.find((p) => p.id === id)?.name || id).join(", ");
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        {variant === "player" ? "Player Pop-up Announcements" : "Public Homepage Pop-ups"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {variant === "player"
          ? "Pop-ups appear as a modal dialog when players open their dashboard. Each player must click “Got it” to dismiss. Once confirmed, the pop-up won't appear again for that player."
          : "Pop-ups appear to anyone visiting the public homepage — great for celebrating tournament winners with a photo, announcing news, or promoting upcoming events. Once a visitor dismisses one, it won't appear again in that browser."}
      </Typography>

      {/* Create form */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon fontSize="small" /> New Pop-up
        </Typography>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder={variant === "player" ? "e.g. Important: Season rule change" : "e.g. Congrats to our Tournament Winners! 🏆"}
        />
        <TextField
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          multiline
          rows={4}
          sx={{ mb: 2 }}
          placeholder="Write the full message here..."
        />

        {/* Image upload */}
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Image (optional)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageSelect} />
          <Button
            variant="outlined"
            size="small"
            startIcon={uploadingImage ? <CircularProgress size={16} /> : <ImageIcon />}
            disabled={uploadingImage}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadingImage ? "Uploading..." : imageUrl ? "Replace image" : "Upload image"}
          </Button>
          {imageUrl && (
            <Box sx={{ position: "relative" }}>
              <Box component="img" src={imageUrl} alt="" sx={{ height: 56, width: 84, objectFit: "cover", borderRadius: 1 }} />
              <IconButton
                size="small"
                onClick={() => setImageUrl(undefined)}
                sx={{ position: "absolute", top: -8, right: -8, bgcolor: "background.paper", boxShadow: 1, "&:hover": { bgcolor: "background.paper" } }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          )}
        </Box>

        <TextField
          label="Button Link URL (optional)"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder="/view-tournaments or https://example.com"
        />
        <TextField
          label="Button Label"
          value={linkLabel}
          onChange={(e) => setLinkLabel(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder="Open link"
        />

        {/* Design picker */}
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Design
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {POPUP_STYLE_LIST.map((styleId) => {
            const cfg = POPUP_STYLE_CONFIG[styleId];
            const selected = style === styleId;
            return (
              <Box
                key={styleId}
                onClick={() => setStyle(styleId)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  minWidth: 110,
                  textAlign: "center",
                  background: cfg.headerBg,
                  color: cfg.headerColor,
                  border: selected ? "3px solid #000" : "3px solid transparent",
                  boxShadow: selected ? 3 : 0,
                  transition: "border 0.15s, box-shadow 0.15s",
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {cfg.emoji} {cfg.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Live preview — matches exactly what visitors/players will see */}
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Preview
        </Typography>
        <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden", borderRadius: 2 }}>
          <PopupHeader style={style} title={title.trim() || "Pop-up title"} imageUrl={imageUrl} size="hero" />
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
              {message.trim() || "Pop-up message will appear here..."}
            </Typography>
            <Button size="small" variant="contained" disabled sx={{ bgcolor: POPUP_STYLE_CONFIG[style].buttonColor }}>
              {variant === "player" ? "Got it, I've read this ✓" : "Got it! 🎉"}
            </Button>
          </Box>
        </Paper>

        {variant === "player" && (
          <>
            {/* Target picker */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Send to</InputLabel>
              <Select value={targetType} label="Send to" onChange={(e) => setTargetType(e.target.value as PopupTargetType)}>
                <MenuItem value="all">All Players</MenuItem>
                <MenuItem value="players">Specific Players</MenuItem>
                <MenuItem value="division">Specific Division(s)</MenuItem>
              </Select>
            </FormControl>

            {targetType === "players" && (
              <Autocomplete
                multiple
                options={players}
                getOptionLabel={(player) => player.name}
                value={targetPlayers}
                onChange={(_, value) => setTargetPlayers(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Target Players" placeholder="Search players..." />}
                sx={{ mb: 2 }}
              />
            )}

            {targetType === "division" && (
              <Autocomplete
                multiple
                options={divisionOptions}
                value={targetDivisions}
                onChange={(_, value) => setTargetDivisions(value)}
                renderInput={(params) => <TextField {...params} label="Target Divisions" placeholder="Select divisions..." />}
                sx={{ mb: 2 }}
              />
            )}
          </>
        )}

        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || uploadingImage || !title.trim() || !message.trim() || !isTargetValid()}
          startIcon={<AddIcon />}
        >
          {creating ? "Creating..." : `Create & Send to ${audienceLabel()}`}
        </Button>
      </Paper>

      {/* Existing popups */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Existing Pop-ups ({popups.length})
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : popups.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          No pop-ups created yet.
        </Typography>
      ) : (
        <List disablePadding>
          {popups.map((popup, idx) => (
            <React.Fragment key={popup.id}>
              {idx > 0 && <Divider />}
              <ListItem alignItems="flex-start" sx={{ px: 0, flexDirection: "column" }}>
                <Box sx={{ display: "flex", width: "100%", alignItems: "flex-start", gap: 1.5 }}>
                  {popup.imageUrl && (
                    <Box
                      component="img"
                      src={popup.imageUrl}
                      alt=""
                      sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1, flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                      <Typography fontWeight={600}>{popup.title}</Typography>
                      <Chip label={popup.isActive ? "Active" : "Inactive"} size="small" color={popup.isActive ? "success" : "default"} />
                      {variant === "player" && <Chip label={`${popup.acknowledgedCount || 0} read`} size="small" variant="outlined" />}
                      <Chip
                        label={`${POPUP_STYLE_CONFIG[popup.style || "notice"].emoji} ${POPUP_STYLE_CONFIG[popup.style || "notice"].label}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: POPUP_STYLE_CONFIG[popup.style || "notice"].buttonColor, color: POPUP_STYLE_CONFIG[popup.style || "notice"].buttonColor }}
                      />
                      {variant === "player" && (
                        <Tooltip title={popupAudienceTooltip(popup)}>
                          <Chip label={popupAudienceLabel(popup)} size="small" variant="outlined" />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {popup.message.length > 120 ? popup.message.slice(0, 120) + "…" : popup.message}
                    </Typography>
                    {popup.linkUrl && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Button: {popup.linkLabel || "Open link"} -&gt; {popup.linkUrl}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Created {new Date(popup.createdAt).toLocaleDateString()} by {popup.createdBy}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <Tooltip title={popup.isActive ? "Deactivate (stop showing to new visitors)" : "Reactivate"}>
                      <Switch size="small" checked={popup.isActive} onChange={(e) => handleToggle(popup.id, e.target.checked)} />
                    </Tooltip>
                    {variant === "player" && (
                      <Tooltip title="Show who read this">
                        <IconButton size="small" onClick={() => setExpandedPopup(expandedPopup === popup.id ? null : popup.id)}>
                          {expandedPopup === popup.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete pop-up">
                      <IconButton size="small" color="error" onClick={() => handleDelete(popup.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {variant === "player" && (
                  <Collapse in={expandedPopup === popup.id} sx={{ width: "100%", mt: 1 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        Read by {popup.acknowledgedCount || 0} player{(popup.acknowledgedCount || 0) !== 1 ? "s" : ""}:
                      </Typography>
                      {(popup.acknowledgments || []).length === 0 ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          No one yet.
                        </Typography>
                      ) : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {(popup.acknowledgments || []).map((ack) => (
                            <Chip
                              key={ack.playerId}
                              label={`${ack.playerName} · ${new Date(ack.acknowledgedAt).toLocaleDateString()}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Collapse>
                )}
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default PopupEditorPanel;
